/* eslint-disable no-nested-ternary */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import SearchIcon from "@mui/icons-material/Search";
import { useAuth } from "react-oidc-context";
import _ from "lodash";
import LoadingIcon from "../../../components/Loading";
import "./multiLocQuoteDetailsPanel.css";
import { NitroMarkerBase } from "../../../components/map/components/NitroMarker";
import TruckIconFO from "../../../icons/TruckIconFO";
import RailIconFO from "../../../icons/RailIconFO";

import { icons } from "../../../svg";
import { useNitroStore } from "../../../store/Store";
import { useNitroCAStore } from "../../../store/CompetitiveAnalysisStore";
import NitroService from "../../../utils/nitro.service";
import NitroRequestService from "../../../utils/nitroRequest.service";

import TroubleshootQuote from "../../multiLocationQuotePanel/components/TroubleshootQuote";
import {
  MultiQuoteDetailsPanelProps,
  Quote,
  AddMissingProductModalState,
} from "../../../types/IMultiLocationQuoteMobile";
import { IRailRateRequestForm } from "../../../types/IRailRateRequest";
import type { MissingRailFreightSectionItem } from "../../../types/IMissingRailFreight";

import FreightDetailPanelQMD from "../../multiLocationQuotePanel/components/RailFreightDetailPanel";
import TruckFreightDetailPanel from "../../multiLocationQuotePanel/components/TruckFreightDetailPanel";
import {
  calculateStations,
  updateInboundModes,
} from "../../multiLocationQuotePanel/components/multiLocationQuoteUtils";
import { sortQuotesByNetbackAndDlvd, toModes } from "../../../utils/utils";
import {
  generateSplitAwareMonths,
  filterTruckQuotes,
  filterRailQuotes,
  filterQuotesWithFreight,
  filterQuotesWithoutFreight,
  filterQuotesByOutboundMode,
  getEnabledOriginIds,
  buildMarketerName,
  buildDestinationsOriginsRequest,
  getQuoteIdentityKey,
  loadRailMissingFreightSections as loadRailMissingFreightSectionsUtil,
  getLocationDetailsWithRetry as getLocationDetailsWithRetryUtil,
  postCreateRailRequests as postCreateRailRequestsUtil,
  submitRailRateRequest,
  loadMissingFreightSectionsWithState,
  handleRailStationChangeWithState,
  handleLookupRailStationNextWithState,
  handleLookupCarrierReviewNextWithState,
  handleSaveRailWithState,
  getOriginsMissingProductQuotes,
  NoGuidanceSectionItem,
  applyAddMissingProductWithState,
  submitAddMissingProductForSelectedOccupantsWithState,
  handleAddMissingProductWithState,
  requestSingleRailRateForOriginWithState,
  sortQuotesWithFreight,
  KochDlvdMode,
  resolveKochDlvdModeFromDestination,
  saveKochDlvdOptions,
} from "./multiLocQuoteDetailsUtils";
import MultiLocQuoteRow from "../multiLocQuotePanel/components/MultiLocQuoteRow";
import OccupantPickerModal from "../multiLocQuotePanel/components/OccupantPickerModal";
import RailStationLookupPanel from "../railStationLookupPanel/RailStationLookupPanel";
import RailStationCarrierReviewPanel from "../railStationLookupPanel/RailStationCarrierReviewPanel";
import RailStationAddCarrierPanel from "../railStationLookupPanel/RailStationAddCarrierPanel";
import AddModeModal from "../multiLocQuotePanel/components/AddModeModal";
import AllOriginsHeader from "../multiLocQuotePanel/components/AllOriginsHeader";
import ProductSelectionSheet from "../multiLocQuotePanel/components/ProductSelectionSheet";
import MonthSelectionSheet from "../multiLocQuotePanel/components/MonthSelectionSheet";
import FooterSelectorButtons from "../multiLocQuotePanel/components/FooterSelectorButtons";
import RouteValidationService from "../../competitiveAnalysisModal/components/RouteValidation/lib/service";
import IRouteValidationResult from "../../competitiveAnalysisModal/components/RouteValidation/types/IRouteValidation";
import { IMultiRouteValidationParams } from "../../../types/IMissingRailFreight";
import { usePublicTariffCheck } from "../manageRailRatePanel/usePublicTariffCheck";
import RailRateRequestPanel from "../manageRailRatePanel/railRateRequestPanel";
import RequestMultipleRailRatesPanel from "../manageRailRatePanel/requestMultipleRailRatesPanel";
import MultiLocQuoteMissingOrigin from "../multiLocQuotePanel/components/multiLocQuoteMissingOrigin";
import { useIsMobile } from "../../../hooks/useIsMobile";
import ILocation from "../../../types/ILocation";

const MultiLocQuoteDetailsPanel: React.FC<MultiQuoteDetailsPanelProps> = ({
  item,
  onBack,
  onSearchClick,
  companyName = "",
  selectedProduct = "AMMONIA",
  setSelectedProduct,
  selectedMonth = "",
  setSelectedMonth,
  onOpenOrigins,
  loading = false,
  locationValidationTrigger = 0,
}) => {
  const auth = useAuth();
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isMonthPickerOpen, setIsMonthPickerOpen] = useState(false);
  const isMobile = useIsMobile();

  const [addModeModalOpen, setAddModeModalOpen] = useState<
    "TRUCK" | "RAIL" | null
  >(null);
  const [railStation, setRailStation] = useState<string>("");
  const [railCarriers, setRailCarriers] = useState<Record<string, boolean>>({});
  const [loadingRailStations, setLoadingRailStations] = useState(false);
  const [loadingCarriers, setLoadingCarriers] = useState(false);
  const [savingRail, setSavingRail] = useState(false);
  const [railStations, setRailStations] = useState<
    { name: string; value: string }[]
  >([]);
  const [showRailStationLookupPanel, setShowRailStationLookupPanel] =
    useState(false);
  const [prefilledRailStation, setPrefilledRailStation] = useState("");
  const [lookupRailStations, setLookupRailStations] = useState<
    { name: string; value: string }[]
  >([]);
  const [loadingLookupRailStations, setLoadingLookupRailStations] =
    useState(false);
  const [showRailCarrierReviewPanel, setShowRailCarrierReviewPanel] =
    useState(false);
  const [selectedLookupStation, setSelectedLookupStation] = useState("");
  const [selectedLookupScrsId, setSelectedLookupScrsId] = useState("");
  const [selectedLookupStationState, setSelectedLookupStationState] =
    useState("");
  const [lookupRailCarriers, setLookupRailCarriers] = useState<
    Record<string, boolean>
  >({});
  const [lookupUserAddedCarriers, setLookupUserAddedCarriers] = useState<
    string[]
  >([]);
  const [loadingLookupRailCarriers, setLoadingLookupRailCarriers] =
    useState(false);
  const [showRailAddCarrierPanel, setShowRailAddCarrierPanel] = useState(false);
  const [showFinishOnRailCarrierReview, setShowFinishOnRailCarrierReview] =
    useState(false);
  const [isLookupDisplayStationsEmpty, setIsLookupDisplayStationsEmpty] =
    useState(false);

  const [
    priorityProducts,
    locations,
    product,
    products,
    favoriteOriginsByUser,
    favoriteToggleOverrides,
    featureFlags,
    currency,
    uom,
    currentFilters,
    setSelectedLocation,
    setTempPinLocation,
    setShowCompetitiveAnalysisModal,
    openInsightsPanel,
    savedNotification,
    failedNotification,
    setMode,
    setFetchFavoriteOrigins,
    logisticsUpdatedModes,
    setLogisticsUpdatedModes,
  ] = useNitroStore((state) => [
    state.priorityProducts,
    state.locations,
    state.product,
    state.products,
    state.favoriteOriginsByUser,
    state.favoriteToggleOverrides,
    state.featureFlags,
    state.currency,
    state.uom,
    state.currentFilters,
    state.setSelectedLocation,
    state.setTempPinLocation,
    state.setShowCompetitiveAnalysisModal,
    state.openInsightsPanel,
    state.savedNotification,
    state.failedNotification,
    state.setMode,
    state.setFetchFavoriteOrigins,
    state.logisticsUpdatedModes,
    state.setLogisticsUpdatedModes,
  ]);
  const { manageRailRate, originManager, multiRailStation } = featureFlags;
  const [
    setDisplayFreightDetailsPanel,
    troubleshootQuoteOpen,
    setTroubleshootQuoteOpen,
    setOriginToTroubleshoot,
    setDestinationToTroubleshoot,
    setSelectedTruckDetailRecord,
    setDisplayTruckFreightDetailsPanel,
    setAnchorForTruckDetailsPanel,
    displayRailFreightDetailsPanel,
    displayTruckFreightDetailsPanel,
    selectedTruckDetailRecord,
    triggerRefetchMultiLocation,
    refetchMultiLocation,
  ] = useNitroCAStore((state) => [
    state.setDisplayRailFreightDetailsPanel,
    state.troubleshootQuoteOpen,
    state.setTroubleshootQuoteOpen,
    state.setOriginToTroubleshoot,
    state.setDestinationToTroubleshoot,
    state.setSelectedTruckDetailRecord,
    state.setDisplayTruckFreightDetailsPanel,
    state.setAnchorForTruckDetailsPanel,
    state.displayRailFreightDetailsPanel,
    state.displayTruckFreightDetailsPanel,
    state.selectedTruckDetailRecord,
    state.triggerRefetchMultiLocation,
    state.refetchMultiLocation,
  ]);

  const [dlvdOfferInput, setDlvdOfferInput] = useState("");
  const [nbOverrides, setNbOverrides] = useState<Map<string, number>>(
    new Map(),
  );
  const [kochDlvdMode, setKochDlvdMode] = useState<{
    truck: "px" | "alt";
    rail: "px" | "alt";
  }>({ truck: "px", rail: "px" });
  const [isSavingDlvdOptions, setIsSavingDlvdOptions] = useState(false);

  // picks the origin-manager-aware or legacy payload/service call based on the feature flag
  const fetchDestinationQuotes = useCallback(
    async (
      request: ReturnType<typeof buildDestinationsOriginsRequest>,
    ): Promise<any[]> => {
      if (originManager) {
        return NitroService.getQuotesByDestination(request, 0, 0);
      }
      return NitroService.getQuotesByDestinationOld(request, 0, 0);
    },
    [originManager],
  );

  const [missingFreightTab, setMissingFreightTab] = useState<string>("TRUCK");
  const [loadingAddProductByKey, setLoadingAddProductByKey] = useState<
    Record<string, boolean>
  >({});
  const [missingMilesOrigins, setMissingMilesOrigins] = useState<
    Array<{
      originId: string;
      originName: string;
      competitor: boolean;
      validation: IRouteValidationResult;
    }>
  >([]);
  const [missingProductQuotes, setMissingProductQuotes] = useState<Quote[]>([]);
  const [refreshedQuoteByKey, setRefreshedQuoteByKey] = useState<
    Record<string, Quote>
  >({});
  const [destinationQuotesOverride, setDestinationQuotesOverride] = useState<
    Quote[] | null
  >(null);
  const [localDestinationModesOverride, setLocalDestinationModesOverride] =
    useState<any>(null);
  const [destinationFetchReady, setDestinationFetchReady] = useState(false);
  const [destinationIsCity, setDestinationIsCity] = useState(
    item.destination?.isCity ?? false,
  );
  const [locallyValidatedOriginKeys, setLocallyValidatedOriginKeys] = useState<
    Set<string>
  >(new Set());
  const [originsWithNoGuidance, setOriginsWithNoGuidance] = useState<
    NoGuidanceSectionItem[]
  >([]);
  const [originsMissingRailFreight, setOriginsMissingRailFreight] = useState<
    MissingRailFreightSectionItem[]
  >([]);
  const [loadingRailMissingFreight, setLoadingRailMissingFreight] =
    useState(false);
  const [loadingMissingMiles, setLoadingMissingMiles] = useState(false);
  const [loadingMileageRequestByOrigin, setLoadingMileageRequestByOrigin] =
    useState<Record<string, boolean>>({});
  const [showRailRateRequestPanel, setShowRailRateRequestPanel] =
    useState(false);
  const [showRequestMultiplePanel, setShowRequestMultiplePanel] =
    useState(false);
  const [
    selectedRailRateRequestOriginName,
    setSelectedRailRateRequestOriginName,
  ] = useState("");
  const [selectedRailRateRequestOrigins, setSelectedRailRateRequestOrigins] =
    useState<string[]>([]);
  const [
    selectedRailRateRequestSubmitOrigins,
    setSelectedRailRateRequestSubmitOrigins,
  ] = useState<string[]>([]);
  const [
    pendingRailRateRequestOriginKeys,
    setPendingRailRateRequestOriginKeys,
  ] = useState<string[]>([]);
  const [requestedRailRateByOriginKey, setRequestedRailRateByOriginKey] =
    useState<Record<string, boolean>>({});
  const [
    failedRailRateRequestByOriginKey,
    setFailedRailRateRequestByOriginKey,
  ] = useState<Record<string, boolean>>({});
  const [
    loadingRailRateRequestByOriginKey,
    setLoadingRailRateRequestByOriginKey,
  ] = useState<Record<string, boolean>>({});
  const [addMissingProductModalState, setAddMissingProductModalState] =
    useState<AddMissingProductModalState | null>(null);
  const [selectedOccupantKeys, setSelectedOccupantKeys] = useState<string[]>(
    [],
  );
  const [submittingAddMissingProduct, setSubmittingAddMissingProduct] =
    useState(false);

  const editableRefs = useRef<Map<string, HTMLSpanElement>>(new Map());
  const debounceTimers = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const missingFreightLoadInFlightRef = useRef(false);
  const pendingMissingFreightReloadTimerRef = useRef<NodeJS.Timeout | null>(
    null,
  );
  const pendingMissingFreightThrottleTimerRef = useRef<NodeJS.Timeout | null>(
    null,
  );
  const autoStartedPublicTariffChecksRef = useRef<Set<string>>(new Set());
  const addMissingProductInFlightRef = useRef(false);
  const addMissingProductCooldownUntilRef = useRef(0);
  const lastMissingFreightLoadAtRef = useRef(0);
  const initialRefetchMultiLocationRef = useRef(refetchMultiLocation);

  const wait = (ms: number) =>
    new Promise((resolve) => {
      setTimeout(resolve, ms);
    });

  const handleKochDlvdModeChange = useCallback(
    async (nextMode: KochDlvdMode) => {
      if (isSavingDlvdOptions) {
        return;
      }

      const hasChanged =
        nextMode.truck !== kochDlvdMode.truck ||
        nextMode.rail !== kochDlvdMode.rail;

      if (!hasChanged) {
        return;
      }

      setKochDlvdMode(nextMode);
      setIsSavingDlvdOptions(true);

      try {
        await saveKochDlvdOptions(
          nextMode,
          auth.user?.profile?.given_name,
          auth.user?.profile?.family_name,
        );
      } catch (error) {
        console.error("Failed to save dlvd options", error);
      } finally {
        setIsSavingDlvdOptions(false);
      }
    },
    [
      auth.user?.profile?.family_name,
      auth.user?.profile?.given_name,
      isSavingDlvdOptions,
      kochDlvdMode.rail,
      kochDlvdMode.truck,
    ],
  );

  const markRailRateRequested = useCallback((originKeys: string[]) => {
    if (!originKeys.length) {
      return;
    }

    setRequestedRailRateByOriginKey((prev) => {
      const next = { ...prev };
      originKeys.forEach((originKey) => {
        next[originKey] = true;
      });
      return next;
    });

    setFailedRailRateRequestByOriginKey((prev) => {
      const next = { ...prev };
      originKeys.forEach((originKey) => {
        delete next[originKey];
      });
      return next;
    });
  }, []);

  const markRailRateRequestFailed = useCallback((originKeys: string[]) => {
    if (!originKeys.length) {
      return;
    }

    setFailedRailRateRequestByOriginKey((prev) => {
      const next = { ...prev };
      originKeys.forEach((originKey) => {
        next[originKey] = true;
      });
      return next;
    });
  }, []);

  const clearRailRateRequestFailed = useCallback((originKeys: string[]) => {
    if (!originKeys.length) {
      return;
    }

    setFailedRailRateRequestByOriginKey((prev) => {
      const next = { ...prev };
      originKeys.forEach((originKey) => {
        delete next[originKey];
      });
      return next;
    });
  }, []);

  const getLocationDetailsWithRetry = useCallback(
    async (locationId: string, maxAttempts = 3): Promise<any> =>
      getLocationDetailsWithRetryUtil({
        locationId,
        maxAttempts,
        fetchLocationDetails: (id) => NitroService.getLocationDetails(id),
      }),
    [],
  );

  const postCreateRailRequests = useCallback(
    async (originsToRequest: Array<{ originId: string; originName: string }>) =>
      postCreateRailRequestsUtil({
        originsToRequest,
        requestedBy:
          buildMarketerName(
            auth.user?.profile?.given_name,
            auth.user?.profile?.family_name,
          ) || "Unknown User",
        destinationStationState: item.destination?.details?.stationState || "",
        destinationCarriers: item.destination?.details?.carriers,
        selectedProduct,
        fallbackProduct: product,
        getLocationDetailsWithRetryFn: (locationId: string) =>
          getLocationDetailsWithRetry(locationId),
      }),
    [
      auth.user?.profile?.family_name,
      auth.user?.profile?.given_name,
      getLocationDetailsWithRetry,
      item.destination?.details?.carriers,
      item.destination?.details?.stationState,
      product,
      selectedProduct,
    ],
  );

  const replaceCityOrLocationInUrl = (
    originalUrl: string,
    newLocation: MissingRailFreightSectionItem,
  ): string => {
    const trimmedLocation = newLocation.originName.trim();
    const url = new URL(originalUrl);
    const params = url.searchParams;
    const queryKey = newLocation.isCity ? "city" : "location";

    params.delete("location");
    params.delete("city");
    params.delete("party");
    params.append(queryKey, trimmedLocation);
    url.search = params.toString();

    return url.toString();
  };

  const locationDetailsCache = useRef<Map<string, Promise<any>>>(new Map());
  const pendingBatchIdsRef = useRef<Set<string>>(new Set());
  const pendingBatchFlushRef = useRef<Promise<void> | null>(null);
  const previousMultiRailStationRef = useRef(multiRailStation);

  useEffect(() => {
    if (previousMultiRailStationRef.current === multiRailStation) return;
    previousMultiRailStationRef.current = multiRailStation;
    locationDetailsCache.current.clear();
  }, [multiRailStation]);

  const getLocationDetailsBatchWithRetry = useCallback(
    async (
      locationIds: string[],
      maxAttempts = 3,
    ): Promise<Map<string, any>> => {
      const idsToFetch = Array.from(
        new Set(locationIds.filter((id) => Boolean(id))),
      );

      if (!idsToFetch.length) {
        return new Map();
      }

      const attemptFetch = async (
        attempt: number,
      ): Promise<Map<string, any>> => {
        try {
          const useMultiRailStation =
            useNitroStore.getState().featureFlags.multiRailStation;
          const results = useMultiRailStation
            ? await NitroService.getMultiLocationDetails2({ ids: idsToFetch })
            : await NitroService.getMultiLocationDetails({ ids: idsToFetch });

          const detailsById = new Map<string, any>();
          (results || []).forEach((record: any) => {
            if (record?.id) {
              detailsById.set(record.id, record);
            }
          });

          idsToFetch.forEach((id) => {
            if (!detailsById.has(id)) {
              detailsById.set(id, undefined);
            }
          });

          return detailsById;
        } catch (error) {
          if (attempt >= maxAttempts) {
            throw error;
          }

          await wait(200 * attempt);
          return attemptFetch(attempt + 1);
        }
      };

      return attemptFetch(1);
    },
    [],
  );

  const getMultiLocationDetailsMap = useCallback(
    async (locationIds: string[]): Promise<Map<string, any>> => {
      const uniqueIds = Array.from(
        new Set(locationIds.filter((id) => Boolean(id))),
      );
      if (!uniqueIds.length) return new Map();

      const uncachedIds = uniqueIds.filter(
        (id) => !locationDetailsCache.current.has(id),
      );

      if (uncachedIds.length > 0) {
        uncachedIds.forEach((id) => pendingBatchIdsRef.current.add(id));

        if (!pendingBatchFlushRef.current) {
          pendingBatchFlushRef.current = Promise.resolve().then(() => {
            pendingBatchFlushRef.current = null;
            const batchIds = Array.from(pendingBatchIdsRef.current);
            pendingBatchIdsRef.current = new Set();

            if (!batchIds.length) return;

            const batchPromise = getLocationDetailsBatchWithRetry(
              batchIds,
            ).catch(() => new Map<string, any>());
            batchIds.forEach((id) => {
              locationDetailsCache.current.set(
                id,
                batchPromise.then((map) => map.get(id)),
              );
            });
          });
        }

        await pendingBatchFlushRef.current;
      }

      const resultMap = new Map<string, any>();
      await Promise.all(
        uniqueIds.map(async (id) => {
          const details = await locationDetailsCache.current.get(id);
          resultMap.set(id, details);
        }),
      );
      return resultMap;
    },
    [getLocationDetailsBatchWithRetry],
  );

  const attachOriginDetailsToQuotes = useCallback(
    async (rawQuotes: Quote[]): Promise<Quote[]> => {
      const originIds = rawQuotes
        .map((quote) => quote?.origin?.id)
        .filter((id): id is string => Boolean(id));

      if (!originIds.length) return rawQuotes;

      try {
        const detailsById = await getMultiLocationDetailsMap(originIds);
        return rawQuotes.map((quote) => {
          const details = quote?.origin?.id
            ? detailsById.get(quote.origin.id)
            : undefined;
          if (!details) return quote;
          return {
            ...quote,
            origin: {
              ...quote.origin,
              details,
              pinType: quote.origin.pinType ?? details.pinType,
              city: quote.origin.city ?? details.city,
            },
          };
        });
      } catch {
        return rawQuotes;
      }
    },
    [getMultiLocationDetailsMap],
  );

  const getLocationDetailsViaMulti = useCallback(
    (locationId: string): Promise<any> =>
      getMultiLocationDetailsMap([locationId]).then((map) =>
        map.get(locationId),
      ),
    [getMultiLocationDetailsMap],
  );

  const publicTariffQuoteParams = useMemo(
    () =>
      buildDestinationsOriginsRequest({
        currentFilters,
        destinationName: item.destination?.name,
        selectedProduct,
        fallbackProduct: product,
        selectedMonth,
        currency,
        uom,
        marketer: buildMarketerName(
          auth.user?.profile?.given_name,
          auth.user?.profile?.family_name,
        ),
      }),
    [
      auth.user?.profile?.family_name,
      auth.user?.profile?.given_name,
      currency,
      currentFilters,
      item.destination?.name,
      product,
      selectedMonth,
      selectedProduct,
      uom,
    ],
  );

  const handleCompetitorPublicTariffFreightFound = useCallback(
    (railOrigin: MissingRailFreightSectionItem, quoteWithFreight: any) => {
      const competitorOrigin = railOrigin.competitor
        ? railOrigin
        : originsMissingRailFreight.find(
            (originEntry) =>
              originEntry.competitor &&
              originEntry.originName === railOrigin.originName,
          );

      if (!competitorOrigin) {
        return;
      }

      const quoteKey = getQuoteIdentityKey(competitorOrigin.quote);
      const mergedQuote = {
        ...competitorOrigin.quote,
        ...quoteWithFreight,
        competitor: true,
        origin: {
          ...(quoteWithFreight?.origin || {}),
          ...competitorOrigin.quote.origin,
        },
        freight: quoteWithFreight?.freight || competitorOrigin.quote?.freight,
      } as Quote;

      setRefreshedQuoteByKey((prev) => ({
        ...prev,
        [quoteKey]: mergedQuote,
      }));

      setOriginsMissingRailFreight((prev) =>
        prev.filter(
          (originEntry) => originEntry.originKey !== competitorOrigin.originKey,
        ),
      );
    },
    [originsMissingRailFreight],
  );

  const {
    publicTariffStatusByOriginKey,
    publicTariffRateByOriginKey,
    handleCheckPublicTariffForOrigin,
  } = usePublicTariffCheck({
    quoteParams: publicTariffQuoteParams,
    destination: item.destination,
    selectedProduct,
    product,
    selectedMonth,
    originManager,
    getLocationDetailsWithRetry: getLocationDetailsViaMulti,
    onCompetitorFreightFound: handleCompetitorPublicTariffFreightFound,
  });

  const schedulePublicTariffCheck = useCallback(
    (origins: MissingRailFreightSectionItem[]) => {
      if (!origins.length) {
        return;
      }

      setTimeout(() => {
        origins.forEach((originEntry) => {
          handleCheckPublicTariffForOrigin(originEntry);
        });
      }, 2000);
    },
    [handleCheckPublicTariffForOrigin],
  );

  const handleOpenInsightsPanel = useCallback(async () => {
    let selectedFavoriteOrigin = _.find(locations, (location) => {
      return location.id === item.destination.id;
    });

    const destinationLocationId =
      item.destination.id ||
      item.destination.details?.id ||
      selectedFavoriteOrigin?.id;

    if (!destinationLocationId) {
      return;
    }

    if (!selectedFavoriteOrigin) {
      selectedFavoriteOrigin = {
        id: destinationLocationId,
        name: item.destination.name,
        lat: item.destination.coordinate?.[1] || 0,
        long: item.destination.coordinate?.[0] || 0,
        pinType: item.destination.pinType || "",
        city: item.destination.city || "",
        stateOrProvince: item.destination.stateOrProvince || "",
        ...item.destination,
      };
    }

    if (
      selectedFavoriteOrigin &&
      selectedFavoriteOrigin.pinType === "zipCodes"
    ) {
      return;
    }

    if (!selectedFavoriteOrigin) {
      console.warn("Overview target not found in locations", {
        destinationLocationId,
      });
      try {
        const details = await NitroService.getLocationDetails(
          destinationLocationId,
        );
        const lat = Number(details?.coordinates?.[0]);
        const lng = Number(details?.coordinates?.[1]);

        if (Number.isFinite(lat) && Number.isFinite(lng)) {
          setTempPinLocation({ lat, lng });
        }
      } catch (err) {
        console.error("Failed to load overview location details", err);
      }

      openInsightsPanel(destinationLocationId, "Logistics", false);
      return;
    }

    setShowCompetitiveAnalysisModal(false);
    setDisplayFreightDetailsPanel(false);
    setSelectedLocation(selectedFavoriteOrigin);
    setTempPinLocation({
      lat: selectedFavoriteOrigin.lat,
      lng: selectedFavoriteOrigin.long,
    });

    openInsightsPanel(destinationLocationId, "Logistics", false);
  }, [
    locations,
    item.destination,
    setShowCompetitiveAnalysisModal,
    setDisplayFreightDetailsPanel,
    setSelectedLocation,
    setTempPinLocation,
    openInsightsPanel,
  ]);

  const enabledOriginIds = useMemo(() => {
    const prod = selectedProduct || product || "AMMONIA";
    return getEnabledOriginIds(
      favoriteOriginsByUser,
      prod,
      favoriteToggleOverrides,
    );
  }, [
    favoriteOriginsByUser,
    product,
    selectedProduct,
    favoriteToggleOverrides,
  ]);

  // Prefetch LocationDetails for enabled favorite origins once when panel loads
  useEffect(() => {
    try {
      const idsToPrefetch = new Set<string>();
      enabledOriginIds.truckEnabled.forEach((key) =>
        idsToPrefetch.add(key.replace(/^koch-|^competitor-/, "")),
      );
      enabledOriginIds.railEnabled.forEach((key) =>
        idsToPrefetch.add(key.replace(/^koch-|^competitor-/, "")),
      );

      if (idsToPrefetch.size > 0) {
        getMultiLocationDetailsMap(Array.from(idsToPrefetch)).catch(() => {});
      }
    } catch (e) {
      // ignore
    }
  }, [enabledOriginIds, getMultiLocationDetailsMap]);

  const refreshValidatedOriginQuote = useCallback(
    async (sourceQuote: Quote, originId: string) => {
      const marketerName = buildMarketerName(
        auth.user?.profile?.given_name,
        auth.user?.profile?.family_name,
      );

      const request = buildDestinationsOriginsRequest({
        currentFilters,
        destinationName: item.destination?.name,
        selectedProduct,
        fallbackProduct: product,
        selectedMonth,
        currency,
        uom,
        marketer: marketerName,
      });

      const refreshedDestinations = await fetchDestinationQuotes(request);

      const refreshedDestination = (refreshedDestinations || []).find(
        (destinationEntry: any) =>
          destinationEntry?.destination?.id === item.destination?.id,
      );
      const refreshedQuote = refreshedDestination?.quotes?.find(
        (quoteEntry: any) => {
          const sameOrigin = quoteEntry?.origin?.id === originId;
          const sameCompetitor =
            !!quoteEntry?.competitor === !!sourceQuote.competitor;
          const sameMode =
            (quoteEntry?.mode || "").toUpperCase() ===
            (sourceQuote.mode || "").toUpperCase();
          return sameOrigin && sameCompetitor && sameMode;
        },
      );

      if (!refreshedQuote) {
        return;
      }

      const quoteKey = getQuoteIdentityKey(sourceQuote);
      const mergedQuote = {
        ...sourceQuote,
        ...refreshedQuote,
        origin: {
          ...sourceQuote.origin,
          ...(refreshedQuote.origin || {}),
        },
      } as Quote;

      setRefreshedQuoteByKey((prev) => ({
        ...prev,
        [quoteKey]: mergedQuote,
      }));
    },
    [
      auth.user?.profile?.given_name,
      auth.user?.profile?.family_name,
      currentFilters,
      item.destination?.name,
      item.destination?.id,
      selectedProduct,
      product,
      selectedMonth,
      currency,
      uom,
      fetchDestinationQuotes,
    ],
  );

  const refreshCurrentDestinationQuotes = useCallback(async () => {
    const marketerName = buildMarketerName(
      auth.user?.profile?.given_name,
      auth.user?.profile?.family_name,
    );

    const request = buildDestinationsOriginsRequest({
      currentFilters,
      destinationName: item.destination?.name,
      selectedProduct,
      fallbackProduct: product,
      selectedMonth,
      currency,
      uom,
      marketer: marketerName,
    });

    const refreshedDestinations = await fetchDestinationQuotes(request);

    const refreshedDestination = (refreshedDestinations || []).find(
      (destinationEntry) =>
        destinationEntry.destination.id === item.destination.id,
    );
    const refreshedDestinationQuotes = refreshedDestination?.quotes;

    if (Array.isArray(refreshedDestinationQuotes)) {
      setDestinationQuotesOverride(
        await attachOriginDetailsToQuotes(
          refreshedDestinationQuotes as Quote[],
        ),
      );
      setRefreshedQuoteByKey({});
    }
  }, [
    auth.user?.profile?.given_name,
    auth.user?.profile?.family_name,
    currentFilters,
    item.destination?.name,
    item.destination.id,
    selectedProduct,
    product,
    selectedMonth,
    currency,
    uom,
    fetchDestinationQuotes,
    attachOriginDetailsToQuotes,
  ]);

  useEffect(() => {
    if (refetchMultiLocation === initialRefetchMultiLocationRef.current) return;
    if (logisticsUpdatedModes?.destId === item.destination?.id) return;
    refreshCurrentDestinationQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchMultiLocation]);

  useEffect(() => {
    const fetchFreshDestinationQuotes = async () => {
      try {
        const marketerName = buildMarketerName(
          auth.user?.profile?.given_name,
          auth.user?.profile?.family_name,
        );
        const request = buildDestinationsOriginsRequest({
          currentFilters,
          destinationName: item.destination?.name,
          selectedProduct,
          fallbackProduct: product,
          selectedMonth,
          currency,
          uom,
          marketer: marketerName,
        });
        const freshDestinations = await fetchDestinationQuotes(request);
        const freshDestination = (freshDestinations || []).find(
          (d) => d?.destination?.id === item.destination?.id,
        );

        if (originManager && freshDestination) {
          setKochDlvdMode(resolveKochDlvdModeFromDestination(freshDestination));
        }

        if (freshDestination && Array.isArray(freshDestination?.quotes)) {
          setDestinationQuotesOverride(
            await attachOriginDetailsToQuotes(
              freshDestination.quotes as Quote[],
            ),
          );
        }
        if (typeof freshDestination?.destination?.isCity === "boolean") {
          setDestinationIsCity(freshDestination.destination.isCity);
        }
      } catch {
        // Proceed with existing item.quotes if fetch fails
      } finally {
        setDestinationFetchReady(true);
      }
    };

    fetchFreshDestinationQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDestinationQuotesOverride(null);
    setLocalDestinationModesOverride(null);
    setDestinationFetchReady(false);
  }, [item.destination?.id]);

  useEffect(() => {
    if (
      logisticsUpdatedModes &&
      logisticsUpdatedModes.destId === item.destination?.id
    ) {
      setLocalDestinationModesOverride(logisticsUpdatedModes.modes);
      refreshCurrentDestinationQuotes();
      setLogisticsUpdatedModes(null);
    }
  }, [
    logisticsUpdatedModes,
    item.destination?.id,
    refreshCurrentDestinationQuotes,
    setLogisticsUpdatedModes,
  ]);

  const effectiveQuotes = destinationQuotesOverride || item.quotes;

  const quotesWithFreight = useMemo(() => {
    const mergedQuotes = effectiveQuotes.map((quote) => {
      const quoteKey = getQuoteIdentityKey(quote);
      const refreshedQuote = refreshedQuoteByKey[quoteKey];
      return refreshedQuote || quote;
    });
    const quotesWithFreightData = filterQuotesWithFreight(mergedQuotes);

    const forcedQuotes =
      locallyValidatedOriginKeys.size > 0
        ? mergedQuotes.filter((quote) => {
            if (quote.freight) return false;
            return locallyValidatedOriginKeys.has(getQuoteIdentityKey(quote));
          })
        : [];

    if (!originManager) {
      return [...quotesWithFreightData, ...forcedQuotes].filter((quote) => {
        if (quote.mode?.toUpperCase() !== "RAIL") return true;
        return quote.freight?.totalFreight != null;
      });
    }

    return [...quotesWithFreightData, ...forcedQuotes].filter((quote) => {
      const originId = quote.origin.id;
      if (!originId) return true;
      const mode = quote.mode?.toUpperCase();

      if (mode === "TRUCK") {
        return true;
      }
      if (mode === "RAIL") {
        if (quote.freight?.totalFreight == null) return false;
        return true;
      }
      return true;
    });
  }, [
    effectiveQuotes,
    locallyValidatedOriginKeys,
    originManager,
    refreshedQuoteByKey,
  ]);

  const quotesWithFreightForDisplay = useMemo(() => {
    const seenKeyCounts = new Map<string, number>();
    const sortedQuotes = originManager
      ? sortQuotesByNetbackAndDlvd(quotesWithFreight, kochDlvdMode)
      : sortQuotesWithFreight(quotesWithFreight);

    return sortedQuotes.map((quote) => {
      const baseKey = `${quote.competitor ? "competitor" : "koch"}-${
        quote.origin.id || quote.origin.name
      }-${quote.mode}-${quote.freight?.id || "unknown"}`;
      const occurrence = seenKeyCounts.get(baseKey) || 0;
      seenKeyCounts.set(baseKey, occurrence + 1);

      return {
        quote,
        renderKey: occurrence === 0 ? baseKey : `${baseKey}-${occurrence}`,
      };
    });
  }, [quotesWithFreight, originManager, kochDlvdMode]);

  const bestOffer = useMemo(() => {
    const getEffectiveNetback = (q: Quote): number | null => {
      if (!originManager) return q.maxPrice ?? null;
      const mode = (q.mode || "").toUpperCase();
      const dlvdOption =
        mode === "TRUCK" ? kochDlvdMode.truck : kochDlvdMode.rail;
      const primary =
        dlvdOption === "px"
          ? q.postedPrice
          : mode === "TRUCK"
          ? q.truckPrice
          : q.railPrice;
      const primaryNum =
        typeof primary === "number" && Number.isFinite(primary)
          ? primary
          : null;
      return primaryNum ?? q.maxPrice ?? null;
    };

    const kochQuotes = quotesWithFreight.filter((q) => {
      if (q.competitor === true) return false;
      if ((q.freight?.totalFreight ?? null) === null) return false;
      return getEffectiveNetback(q) !== null;
    });

    if (!kochQuotes.length) return null;

    const sorted = [...kochQuotes].sort((a, b) => {
      const totalA =
        (getEffectiveNetback(a) ?? 0) + (a.freight?.totalFreight || 0);
      const totalB =
        (getEffectiveNetback(b) ?? 0) + (b.freight?.totalFreight || 0);
      return totalA - totalB;
    });

    const best = sorted[0];
    const effectiveNetback = getEffectiveNetback(best) ?? 0;
    const totalFreight = best.freight?.totalFreight ?? 0;

    return {
      deliveredPrice: (effectiveNetback + totalFreight).toFixed(0),
      freight: totalFreight.toFixed(2),
      netback: effectiveNetback.toFixed(0),
      originName: best.origin.name,
      mode: best.mode,
      minCars: (best.freight as any)?.minCars,
    };
  }, [quotesWithFreight, kochDlvdMode, originManager]);

  const quotesWithoutFreight = useMemo(() => {
    const filteredQuotes = effectiveQuotes.filter((quote) => {
      if (item.destination.pinType === "city") {
        return quote.mode?.toUpperCase() === "TRUCK";
      }
      return true;
    });

    return filterQuotesWithoutFreight(filteredQuotes);
  }, [effectiveQuotes, item.destination.pinType]);

  const truckMissingQuotes = useMemo(() => {
    if (originManager) {
      return quotesWithoutFreight.filter(
        (q) => q.mode?.toUpperCase() === "TRUCK",
      );
    }

    return filterTruckQuotes(
      quotesWithoutFreight,
      enabledOriginIds.truckEnabled,
    );
  }, [enabledOriginIds, originManager, quotesWithoutFreight]);

  const railMissingQuotes = useMemo(() => {
    if (originManager) {
      return quotesWithoutFreight.filter(
        (q) => q.mode?.toUpperCase() !== "TRUCK",
      );
    }

    return filterRailQuotes(quotesWithoutFreight, enabledOriginIds.railEnabled);
  }, [enabledOriginIds, originManager, quotesWithoutFreight]);

  const railMissingQuotesForDisplay = useMemo(() => {
    const railInboundEnabled = !!(
      localDestinationModesOverride?.RAIL?.inbound ??
      item.destination.details?.modes?.RAIL?.inbound
    );

    if (railInboundEnabled) {
      return filterQuotesByOutboundMode(railMissingQuotes, "RAIL");
    }

    const destinationId = item.destination?.id;
    if (!destinationId) {
      return filterQuotesByOutboundMode(railMissingQuotes, "RAIL");
    }

    return filterQuotesByOutboundMode(
      railMissingQuotes.filter((quote) => quote.origin?.id !== destinationId),
      "RAIL",
    );
  }, [
    item.destination?.details?.modes?.RAIL?.inbound,
    item.destination?.id,
    localDestinationModesOverride?.RAIL?.inbound,
    railMissingQuotes,
  ]);

  const truckMissingQuotesForDisplay = useMemo(() => {
    return filterQuotesByOutboundMode(truckMissingQuotes, "TRUCK");
  }, [truckMissingQuotes]);

  const hasTruckMissing = truckMissingQuotesForDisplay.length > 0;
  const hasRailMissing = railMissingQuotesForDisplay.length > 0;

  const hasTruckInbound =
    !!item.destination.isCity ||
    !!(
      localDestinationModesOverride?.TRUCK?.inbound ??
      item.destination.details?.inboundModes?.includes("TRUCK")
    );
  const hasRailInbound = !!(
    localDestinationModesOverride?.RAIL?.inbound ??
    item.destination.details?.inboundModes?.includes("RAIL")
  );
  const shouldShowTruckMissing = hasTruckInbound && hasTruckMissing;
  const shouldShowRailMissing = hasRailInbound && hasRailMissing;
  const originsMissingProductForActiveTab = useMemo(() => {
    const activeMode = missingFreightTab === "RAIL" ? "RAIL" : "TRUCK";
    return filterQuotesByOutboundMode(
      getOriginsMissingProductQuotes({
        missingFreightTab,
        shouldShowTruckMissing,
        shouldShowRailMissing,
        missingProductQuotes,
      }),
      activeMode,
    );
  }, [
    missingFreightTab,
    missingProductQuotes,
    shouldShowRailMissing,
    shouldShowTruckMissing,
  ]);
  const showMissingProductHeader = originsMissingProductForActiveTab.length > 0;

  // Don't dispaly the origin in Missing Freight section if it's already being displayed in Missing Product section for the active tab
  const originsMissingProductKeys = useMemo(() => {
    const keys = new Set<string>();
    originsMissingProductForActiveTab.forEach((quote) => {
      const originId = quote.origin?.id;
      if (!originId) return;
      keys.add(`${quote.competitor ? "competitor" : "koch"}-${originId}`);
    });
    return keys;
  }, [originsMissingProductForActiveTab]);
  const originsMissingRailFreightForDisplay = useMemo(
    () =>
      originsMissingRailFreight.filter(
        (originEntry) => !originsMissingProductKeys.has(originEntry.originKey),
      ),
    [originsMissingRailFreight, originsMissingProductKeys],
  );

  useEffect(() => {
    const displayedOriginKeySet = new Set(
      originsMissingRailFreightForDisplay.map(
        (originEntry) => originEntry.originKey,
      ),
    );

    Array.from(autoStartedPublicTariffChecksRef.current).forEach(
      (originKey) => {
        const stillDisplayed = displayedOriginKeySet.has(originKey);
        const status = publicTariffStatusByOriginKey[originKey];
        const stillAutoStartStatus = !status || status === "checking";
        if (!stillDisplayed || !stillAutoStartStatus) {
          autoStartedPublicTariffChecksRef.current.delete(originKey);
        }
      },
    );

    originsMissingRailFreightForDisplay.forEach((originEntry) => {
      const isRailRateRequested =
        !!requestedRailRateByOriginKey[originEntry.originKey] ||
        (originEntry?.railValidation as any)?.railRateRequested === true;
      if (!isRailRateRequested) {
        return;
      }

      const status = publicTariffStatusByOriginKey[originEntry.originKey];
      const shouldAutoStart = !status || status === "checking";
      if (!shouldAutoStart) {
        return;
      }

      if (autoStartedPublicTariffChecksRef.current.has(originEntry.originKey)) {
        return;
      }

      autoStartedPublicTariffChecksRef.current.add(originEntry.originKey);
      handleCheckPublicTariffForOrigin(originEntry);
    });
  }, [
    handleCheckPublicTariffForOrigin,
    originsMissingRailFreightForDisplay,
    publicTariffStatusByOriginKey,
    requestedRailRateByOriginKey,
  ]);

  useEffect(() => {
    if (
      missingFreightTab === "TRUCK" &&
      !shouldShowTruckMissing &&
      shouldShowRailMissing
    ) {
      setMissingFreightTab("RAIL");
      return;
    }

    if (
      missingFreightTab === "RAIL" &&
      !shouldShowRailMissing &&
      shouldShowTruckMissing
    ) {
      setMissingFreightTab("TRUCK");
      return;
    }

    if (!shouldShowTruckMissing && !shouldShowRailMissing) {
      setMissingFreightTab("TRUCK");
    }
  }, [missingFreightTab, shouldShowTruckMissing, shouldShowRailMissing]);

  const handleDlvdOfferChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setDlvdOfferInput(value);
  };

  const handleResetClick = () => {
    setDlvdOfferInput("");
    setNbOverrides(new Map());
  };

  const handleNbEdit = useCallback(
    (quoteKey: string, element: HTMLSpanElement | null) => {
      if (element) {
        const newValue = parseFloat(
          element.textContent?.replace(/[^0-9.-]/g, "") || "0",
        );
        if (!Number.isNaN(newValue)) {
          setNbOverrides((prev) => {
            const newMap = new Map(prev);
            newMap.set(quoteKey, newValue);
            return newMap;
          });
        }
      }
    },
    [],
  );

  useEffect(() => {
    const timers = debounceTimers.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      if (pendingMissingFreightReloadTimerRef.current) {
        clearTimeout(pendingMissingFreightReloadTimerRef.current);
        pendingMissingFreightReloadTimerRef.current = null;
      }
      if (pendingMissingFreightThrottleTimerRef.current) {
        clearTimeout(pendingMissingFreightThrottleTimerRef.current);
        pendingMissingFreightThrottleTimerRef.current = null;
      }
    };
  }, []);

  const handleRailStationChange = useCallback(
    async (newStation: string) =>
      handleRailStationChangeWithState({
        newStation,
        destinationId: item.destination.id,
        getLocationDetailsWithRetryFn: getLocationDetailsWithRetry,
        getCarriersByRailStation: (params) =>
          NitroService.getCarriersByRailStation(params),
        setRailStation,
        setLoadingCarriers,
        setRailCarriers,
      }),
    [item.destination.id, getLocationDetailsWithRetry],
  );

  useEffect(() => {
    if (addModeModalOpen === "RAIL") {
      const defaultRailStation = prefilledRailStation || "";
      setRailStation(defaultRailStation);
      setRailCarriers({});

      const fetchRailStations = async () => {
        if (item?.destination?.details?.zipCode) {
          setLoadingRailStations(true);
          try {
            const records = await NitroService.getRailStationsByZipCode({
              zipCode: item.destination.details.zipCode,
            });
            const mappedRecords = records
              .slice()
              .sort((a, b) =>
                (a.name || "").localeCompare(b.name || "", undefined, {
                  sensitivity: "base",
                }),
              )
              .map((r) => ({ name: r.name, value: r.name }));

            setRailStations(mappedRecords);

            if (defaultRailStation) {
              const selectedStation = mappedRecords.find(
                (record) => record.value === defaultRailStation,
              );

              if (selectedStation) {
                await handleRailStationChange(selectedStation.value);
              }
            }
          } catch (err) {
            console.error("Error fetching rail stations:", err);
            setRailStations([]);
          }
          setLoadingRailStations(false);
        }
      };

      fetchRailStations();

      if (prefilledRailStation) {
        setPrefilledRailStation("");
      }
    }
  }, [
    addModeModalOpen,
    handleRailStationChange,
    item?.destination?.details?.zipCode,
    prefilledRailStation,
  ]);

  const handleDisplayStationsEmptyChange = useCallback((isEmpty: boolean) => {
    setIsLookupDisplayStationsEmpty(isEmpty);
    setShowFinishOnRailCarrierReview(isEmpty);
  }, []);

  const handleAddInboundRailClick = async () => {
    setIsLookupDisplayStationsEmpty(false);
    setLookupRailStations([]);
    setLoadingLookupRailStations(false);
    setShowRailStationLookupPanel(true);
  };

  const handleLookupRailStationNext = async (station: {
    name: string;
    value: string;
    scrsId?: string | null;
    carriers?: string[];
    stationState?: string;
  }) =>
    handleLookupRailStationNextWithState({
      station,
      destinationId: item.destination.id,
      getLocationDetailsWithRetryFn: getLocationDetailsWithRetry,
      getCarriersByRailStation: (params) =>
        NitroService.getCarriersByRailStation(params),
      setShowFinishOnRailCarrierReview,
      setSelectedLookupStation,
      setSelectedLookupScrsId,
      setSelectedLookupStationState,
      setLookupUserAddedCarriers,
      setShowRailStationLookupPanel,
      setShowRailCarrierReviewPanel,
      setLoadingLookupRailCarriers,
      setLookupRailCarriers,
    });

  const handleLookupCarrierToggle = (carrier: string) => {
    setLookupRailCarriers((prev) => ({
      ...prev,
      [carrier]: !prev[carrier],
    }));
  };

  const handleLookupCarrierReviewNext = async () => {
    await handleLookupCarrierReviewNextWithState({
      lookupRailCarriers,
      destinationId: item.destination.id,
      selectedLookupScrsId,
      selectedLookupStation,
      existingModes: item.destination?.details?.modes || {},
      threeLegsAllowed: item.destination.details?.threeLegsAllowed ?? false,
      equipmentTypes: item.destination.details?.equipmentTypes ?? [],
      updateRailStation: (params) => NitroService.updateRailStation(params),
      updateLocationModes: (params) => NitroService.updateLocationModes(params),
      getLocationDetailsWithRetryFn: getLocationDetailsWithRetry,
      setDestinationModesOnItem: (modes) => {
        if (!item.destination.details) {
          // eslint-disable-next-line no-param-reassign
          item.destination.details = {} as any;
        }
        // eslint-disable-next-line no-param-reassign
        item.destination.details.modes = modes;
      },
      setLocalDestinationModesOverride,
      refreshCurrentDestinationQuotes,
      setShowRailCarrierReviewPanel,
      setShowRailStationLookupPanel,
      setLookupUserAddedCarriers,
      failedNotification,
    });
  };

  const toggleRailCarrier = (carrier: string) => {
    setRailCarriers((prev) => ({
      ...prev,
      [carrier]: !prev[carrier as keyof typeof prev],
    }));
  };

  const handleSaveRail = async () => {
    await handleSaveRailWithState({
      railStation,
      railCarriers,
      destinationId: item.destination.id,
      existingModes: item.destination.details.modes || {},
      equipmentTypes: item.destination.details.equipmentTypes || [],
      updateLogistics: (params) => NitroService.updateLogistics(params),
      getLocationDetailsWithRetryFn: getLocationDetailsWithRetry,
      setDestinationCarriersAndStationOnItem: (
        updatedLocationDetails,
        station,
      ) => {
        if (!item.destination.details) {
          // eslint-disable-next-line no-param-reassign
          item.destination.details = {} as any;
        }
        // eslint-disable-next-line no-param-reassign
        item.destination.details.carriers = updatedLocationDetails?.carriers;
        // eslint-disable-next-line no-param-reassign
        item.destination.details.railStation =
          updatedLocationDetails?.railStation || station;
      },
      setFetchFavoriteOrigins,
      triggerRefetchMultiLocation,
      setAddModeModalOpen,
      setSavingRail,
      savedNotification,
      failedNotification,
    });
  };

  const handleAddInboundTruck = async () => {
    try {
      await updateInboundModes(item.destination as any, true, "TRUCK");

      setFetchFavoriteOrigins(true);
      triggerRefetchMultiLocation();
      savedNotification("Successfully added inbound truck.");
    } catch (err) {
      failedNotification("Failed to add inbound truck.", (err as any).message);
    }
  };

  const splitMonthKeys = useMemo(() => {
    const keys = new Set<string>();
    const productEntry = favoriteOriginsByUser.find(
      (o: any) =>
        o.product?.toUpperCase() ===
        (selectedProduct || "AMMONIA").toUpperCase(),
    );
    (productEntry?.favorites || []).forEach((fav: any) => {
      (fav.months || []).forEach((m: any) => {
        const isSplitMonth =
          typeof m?.isSplit === "boolean"
            ? m.isSplit
            : Array.isArray(m?.periods) && m.periods.length > 1;
        if (isSplitMonth) {
          const mk =
            typeof m.month === "string"
              ? (m.month as string).slice(0, 7)
              : `${(m.month as any).year}-${String(
                  (m.month as any).month,
                ).padStart(2, "0")}`;
          keys.add(mk);
        }
      });
    });
    return Array.from(keys);
  }, [favoriteOriginsByUser, selectedProduct]);

  const months = useMemo(
    () => generateSplitAwareMonths(splitMonthKeys),
    [splitMonthKeys],
  );

  const setTroubleshootContext = (quote: Quote) => {
    const quoteMode = quote.mode?.toUpperCase() || "TRUCK";
    setMode(quoteMode);

    setOriginToTroubleshoot({
      id: quote.origin.id || "",
      name: quote.origin.name,
      coordinate: quote.origin.coordinate || [0, 0],
      isCity: quote.origin.isCity || false,
    });
    setDestinationToTroubleshoot({
      id: item.destination.id,
      name: item.destination.name,
      city: item.destination.city || "",
      stateOrProvince: item.destination.stateOrProvince || "",
      coordinate: item.destination.coordinate || [],
      isCity: destinationIsCity || false,
      pinType: item.destination.pinType || "",
      availableProducts: item.destination.availableProducts || [],
      displayName: item.destination.displayName || item.destination.name,
      details: item.destination.details,
    } as any);
  };

  const handleReviewResolveClick = (quote: Quote) => {
    setTroubleshootContext(quote);
    if (missingFreightTab === "TRUCK") {
      return;
    }
    setTroubleshootQuoteOpen(true);
  };

  const closeAddMissingProductModal = () => {
    if (submittingAddMissingProduct) return;
    setAddMissingProductModalState(null);
    setSelectedOccupantKeys([]);
  };

  const toggleSelectedOccupant = (occupantKey: string) => {
    if (submittingAddMissingProduct) return;

    setSelectedOccupantKeys((prev) => {
      if (prev.includes(occupantKey)) {
        return prev.filter((key) => key !== occupantKey);
      }

      return [...prev, occupantKey];
    });
  };

  const applyAddMissingProduct = async ({
    quote,
    originId,
    addProductKey,
    missingProductName,
    selectedOccupants,
    existingLocationDetails,
  }: {
    quote: Quote;
    originId: string;
    addProductKey: string;
    missingProductName: string;
    selectedOccupants: any[];
    existingLocationDetails?: any;
  }) => {
    // Ensure section recomputation uses fresh occupant data after UpdateOccupants.
    locationDetailsCache.current.delete(originId);
    pendingBatchIdsRef.current.delete(originId);

    await applyAddMissingProductWithState({
      quote,
      originId,
      addProductKey,
      missingProductName,
      selectedOccupants,
      existingLocationDetails,
      addMissingProductInFlight: addMissingProductInFlightRef,
      addMissingProductCooldownUntil: addMissingProductCooldownUntilRef,
      getLocationDetailsWithRetryFn: (locationId) =>
        getLocationDetailsWithRetry(locationId),
      updateOccupants: (params) => NitroService.updateOccupants(params as any),
      selectedProduct,
      fallbackProduct: product,
      destinationId: item.destination.id,
      destinationIsCity: !!destinationIsCity,
      selectedMonth,
      validateRoute: (params) => RouteValidationService.validate(params),
      setLoadingAddProductByKey,
      setMissingProductQuotes,
      setMissingMilesOrigins,
      setOriginsWithNoGuidance,
      setLocallyValidatedOriginKeys,
      refreshValidatedOriginQuote,
      onSuccess: async () => {
        await refreshCurrentDestinationQuotes();
        if (pendingMissingFreightThrottleTimerRef.current) {
          clearTimeout(pendingMissingFreightThrottleTimerRef.current);
          pendingMissingFreightThrottleTimerRef.current = null;
        }
        lastMissingFreightLoadAtRef.current = 0;
        await Promise.all([
          loadMissingFreightSections({ forceReload: true }),
          loadRailMissingFreightSections(),
        ]);
        triggerRefetchMultiLocation();
      },
      savedNotification,
      failedNotification,
    });
  };

  const submitAddMissingProductForSelectedOccupants = async () => {
    await submitAddMissingProductForSelectedOccupantsWithState({
      addMissingProductModalState,
      selectedOccupantKeys,
      addMissingProductInFlight: addMissingProductInFlightRef,
      addMissingProductCooldownUntil: addMissingProductCooldownUntilRef,
      setSubmittingAddMissingProduct,
      setAddMissingProductModalState,
      setSelectedOccupantKeys,
      applyAddMissingProductFn: (params) => applyAddMissingProduct(params),
    });
  };

  const handleAddMissingProduct = (quote: Quote) => {
    handleAddMissingProductWithState({
      quote,
      addMissingProductInFlight: addMissingProductInFlightRef,
      addMissingProductCooldownUntil: addMissingProductCooldownUntilRef,
      selectedProduct,
      fallbackProduct: product,
      getLocationDetailsWithRetryFn: (locationId) =>
        getLocationDetailsWithRetry(locationId),
      setAddMissingProductModalState,
      setSelectedOccupantKeys,
      failedNotification,
      applyAddMissingProductFn: (params) => applyAddMissingProduct(params),
    });
  };

  const validateMultiRoute = useCallback(
    (params: IMultiRouteValidationParams) =>
      destinationIsCity
        ? RouteValidationService.cityMultiValidate(params)
        : RouteValidationService.locationMultiValidate(params),
    [destinationIsCity],
  );

  const loadRailMissingFreightSections = useCallback(async () => {
    await loadRailMissingFreightSectionsUtil({
      manageRailRate,
      missingFreightTab,
      shouldShowRailMissing,
      destinationId: item.destination.id,
      destinationIsCity: item.destination.isCity,
      railMissingQuotesForDisplay,
      selectedProduct: selectedProduct || product || "AMMONIA",
      selectedMonth: selectedMonth || "",
      getMultiLocationDetails: getMultiLocationDetailsMap,
      validateMultiRoute,
      setOriginsMissingRailFreight,
      setLoadingRailMissingFreight,
    });
  }, [
    getMultiLocationDetailsMap,
    item.destination.id,
    item.destination.isCity,
    manageRailRate,
    missingFreightTab,
    product,
    railMissingQuotesForDisplay,
    selectedMonth,
    selectedProduct,
    shouldShowRailMissing,
    validateMultiRoute,
  ]);

  const loadMissingFreightSections = useCallback(
    async (options?: { forceReload?: boolean }) => {
      await loadMissingFreightSectionsWithState({
        missingFreightLoadInFlight: missingFreightLoadInFlightRef,
        lastMissingFreightLoadAt: lastMissingFreightLoadAtRef,
        pendingMissingFreightThrottleTimer:
          pendingMissingFreightThrottleTimerRef,
        minReloadIntervalMs: options?.forceReload ? 0 : 2500,
        destinationId: item.destination.id,
        truckMissingQuotesForDisplay,
        selectedProduct,
        fallbackProduct: product,
        selectedMonth,
        getMultiLocationDetailsMap,
        validateMultiRoute,
        setMissingProductQuotes,
        setMissingMilesOrigins,
        setOriginsWithNoGuidance,
        setLoadingMissingMiles,
        onThrottledReload: () => {
          loadMissingFreightSections();
        },
      });
    },
    [
      item.destination.id,
      product,
      selectedMonth,
      selectedProduct,
      truckMissingQuotesForDisplay,
      getMultiLocationDetailsMap,
      validateMultiRoute,
    ],
  );

  useEffect(() => {
    if (!destinationFetchReady) return;

    if (pendingMissingFreightReloadTimerRef.current) {
      clearTimeout(pendingMissingFreightReloadTimerRef.current);
      pendingMissingFreightReloadTimerRef.current = null;
    }

    pendingMissingFreightReloadTimerRef.current = setTimeout(() => {
      pendingMissingFreightReloadTimerRef.current = null;
      loadMissingFreightSections();
    }, 1200);
  }, [
    destinationFetchReady,
    loadMissingFreightSections,
    locationValidationTrigger,
  ]);

  useEffect(() => {
    if (!destinationFetchReady) return;
    loadRailMissingFreightSections();
  }, [
    destinationFetchReady,
    loadRailMissingFreightSections,
    locationValidationTrigger,
    multiRailStation,
  ]);

  useEffect(() => {
    const requestedOriginKeys = originsMissingRailFreight
      .filter(
        (originEntry) =>
          (originEntry?.railValidation as any)?.railRateRequested === true,
      )
      .map((originEntry) => originEntry.originKey)
      .filter(Boolean);

    if (!requestedOriginKeys.length) {
      return;
    }

    setRequestedRailRateByOriginKey((prev) => {
      const next = { ...prev };
      requestedOriginKeys.forEach((originKey) => {
        next[originKey] = true;
      });
      return next;
    });
  }, [originsMissingRailFreight]);

  const handleMileageRequest = useCallback(
    async (missingMilesOrigin: {
      originId: string;
      originName: string;
      competitor?: boolean;
      validation: IRouteValidationResult;
    }) => {
      const originLoadingKey = `${
        missingMilesOrigin.competitor ? "competitor" : "koch"
      }-${missingMilesOrigin.originId}`;

      setLoadingMileageRequestByOrigin((prev) => ({
        ...prev,
        [originLoadingKey]: true,
      }));

      try {
        const marketer = `${auth.user?.profile?.given_name || ""} ${
          auth.user?.profile?.family_name || ""
        }`.trim();

        await NitroRequestService.postMileageRequest({
          marketer,
          product: selectedProduct || product || "AMMONIA",
          mode: "Truck",
          origin: missingMilesOrigin.originName,
          originZipCode: missingMilesOrigin.validation?.originZipCode ?? "",
          destination: item.destination.name || "",
          destinationZipCode:
            missingMilesOrigin.validation?.destinationZipCode ?? "",
          comments: "",
        });

        await NitroService.updateMileageRequestStatus({
          originZip: missingMilesOrigin.validation?.originZipCode ?? "",
          destinationZip:
            missingMilesOrigin.validation?.destinationZipCode ?? "",
          user: marketer,
        });

        setMissingMilesOrigins((prev) =>
          prev.map((origin) => {
            if (
              origin.originId !== missingMilesOrigin.originId ||
              !!origin.competitor !== !!missingMilesOrigin.competitor
            ) {
              return origin;
            }

            return {
              ...origin,
              validation: {
                ...origin.validation,
                mileageRequested: true,
              },
            };
          }),
        );

        savedNotification("Mileage request sent successfully");
      } catch (error: any) {
        failedNotification(
          "Failed to send mileage request",
          error?.message || "Unknown error",
        );
      } finally {
        setLoadingMileageRequestByOrigin((prev) => ({
          ...prev,
          [originLoadingKey]: false,
        }));
      }
    },
    [
      failedNotification,
      item.destination.name,
      product,
      savedNotification,
      selectedProduct,
      auth.user?.profile?.family_name,
      auth.user?.profile?.given_name,
    ],
  );

  const buildRailRateRequestPayload = useCallback(
    (
      selectedOrigins: MissingRailFreightSectionItem[],
      requestPayload?: IRailRateRequestForm,
    ) => {
      const selectedProductName = (selectedProduct || product || "").trim();
      const selectedProductMeta = products.get(selectedProductName);

      return {
        origins: selectedOrigins.map((originEntry) => ({
          origin: {
            name: originEntry.originDetails?.name || "",
            city:
              `${originEntry.originDetails?.city}, ${originEntry.originDetails?.state}` ||
              "",
            address: originEntry.originDetails?.address || "",
            zipCode: originEntry.originDetails?.zipCode || "",
            stations: calculateStations(
              multiRailStation,
              originEntry.originDetails as ILocation,
            ),
            state: originEntry.originDetails?.state || "",
            link: replaceCityOrLocationInUrl(window.location.href, originEntry),
          },
          competitor: originEntry.competitor === true,
        })),
        destination: {
          name: item.destination.details?.name,
          city:
            `${item.destination.details?.city}, ${item.destination.details?.state}` ||
            "",
          address: item.destination.details?.address || "",
          zipCode: item.destination.details?.zipCode || "",
          stations: calculateStations(
            multiRailStation,
            item.destination.details,
          ),
          state: item.destination.details?.state || "",
          link: window.location.href,
        },
        rateType: "Tariffs Live",
        stcc: String(selectedProductMeta?.stccCodes?.[0] || ""),
        carType: selectedProductMeta?.carType || "",
        requestedBy: `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`,
        product,
        weightType: selectedProductMeta?.traticsWeightType || 0,
        businessOpportunity: requestPayload?.businessOpportunity,
        volume: requestPayload?.volume || "",
        targetRate: requestPayload?.targetRate || "",
        sourcingAndLogistics: requestPayload?.sourcingAndLogistics || "",
        comments: requestPayload?.comments || "",
      };
    },
    [
      auth.user?.profile.family_name,
      auth.user?.profile.given_name,
      item.destination.details,
      multiRailStation,
      product,
      products,
      selectedProduct,
    ],
  );

  const handleRailRateRequestSubmit = useCallback(
    async (
      payload: IRailRateRequestForm,
      pendingOriginKeysOverride?: string[],
    ) =>
      submitRailRateRequest({
        payload,
        originsMissingRailFreight,
        pendingRailRateRequestOriginKeys:
          pendingOriginKeysOverride || pendingRailRateRequestOriginKeys,
        setLoadingRailRateRequestByOriginKey,
        postCreateRailRequestsFn: async (originsToRequest) => {
          try {
            await postCreateRailRequests(originsToRequest);
          } catch (error) {
            console.error("postCreateRailRequestsFn failed", error);
          }
        },
        postRequestForRailRate: async ({ selectedOriginEntries }) => {
          const selectedOriginKeys = selectedOriginEntries
            .map((originEntry) => originEntry.originKey)
            .filter(Boolean);
          clearRailRateRequestFailed(selectedOriginKeys);

          try {
            await NitroRequestService.postRequestForRailRate(
              buildRailRateRequestPayload(selectedOriginEntries, payload),
            );
            schedulePublicTariffCheck(selectedOriginEntries);
          } catch (error) {
            console.error("postRequestForRailRate failed", error);
            markRailRateRequestFailed(selectedOriginKeys);
            throw error;
          }
        },
        markRailRateRequested,
        setPendingRailRateRequestOriginKeys,
        savedNotification,
        failedNotification,
      }),
    [
      failedNotification,
      markRailRateRequested,
      markRailRateRequestFailed,
      originsMissingRailFreight,
      pendingRailRateRequestOriginKeys,
      postCreateRailRequests,
      savedNotification,
      buildRailRateRequestPayload,
      clearRailRateRequestFailed,
      schedulePublicTariffCheck,
    ],
  );

  const handleRequestSingleRailOrigin = useCallback(
    async (railOrigin: MissingRailFreightSectionItem) => {
      if (loadingRailRateRequestByOriginKey[railOrigin.originKey]) {
        return;
      }

      await requestSingleRailRateForOriginWithState({
        railOrigin,
        setLoadingRailRateRequestByOriginKey,
        postCreateRailRequestsFn: async (originsToRequest) => {
          try {
            await postCreateRailRequests(originsToRequest);
          } catch (error) {
            console.error("postCreateRailRequestsFn failed", error);
          }
        },
        postRequestForRailRate: async (selectedOrigins) => {
          const selectedOriginKeys = selectedOrigins
            .map((originEntry) => originEntry.originKey)
            .filter(Boolean);
          clearRailRateRequestFailed(selectedOriginKeys);

          try {
            await NitroRequestService.postRequestForRailRate(
              buildRailRateRequestPayload(selectedOrigins),
            );
            schedulePublicTariffCheck(selectedOrigins);
          } catch (error) {
            console.error("postRequestForRailRate failed", error);
            markRailRateRequestFailed(selectedOriginKeys);
            throw error;
          }
        },
        markRailRateRequested,
        setPendingRailRateRequestOriginKeys,
        setSelectedRailRateRequestOrigins,
        setSelectedRailRateRequestOriginName,
        setShowRailRateRequestPanel,
        savedNotification,
        failedNotification,
      });
    },
    [
      failedNotification,
      loadingRailRateRequestByOriginKey,
      markRailRateRequested,
      markRailRateRequestFailed,
      postCreateRailRequests,
      savedNotification,
      buildRailRateRequestPayload,
      clearRailRateRequestFailed,
      schedulePublicTariffCheck,
    ],
  );

  return (
    <div id="quote-details-panel" className="quote-panel-container">
      <RailStationLookupPanel
        open={showRailStationLookupPanel}
        destinationName={item.destination.name || "Destination"}
        defaultFilterLabel={`${item.destination.city || ""}${
          item.destination.stateOrProvince
            ? `, ${item.destination.stateOrProvince}`
            : ""
        }`}
        selectedScrsId={
          selectedLookupScrsId || item.destination.details?.scrsId || ""
        }
        stations={lookupRailStations}
        loading={loadingLookupRailStations}
        onClose={() => setShowRailStationLookupPanel(false)}
        onNext={handleLookupRailStationNext}
        onDisplayStationsEmptyChange={handleDisplayStationsEmptyChange}
      />

      <RailStationCarrierReviewPanel
        open={showRailCarrierReviewPanel}
        destinationName={item.destination.name || "Destination"}
        stationName={selectedLookupStation}
        locationId={item.destination.id}
        existingModes={
          item.destination?.details?.modes ||
          (toModes(
            item.destination?.details?.inboundModes || [],
            item.destination?.details?.outboundModes || [],
          ) as any)
        }
        isMobileFinishFlow={isMobile}
        multiRailStation={featureFlags.multiRailStation}
        existingRailStations={item.destination.details?.railStations}
        stationState={selectedLookupStationState}
        carriers={lookupRailCarriers}
        loading={loadingLookupRailCarriers}
        userAddedCarriers={lookupUserAddedCarriers}
        threeLegsAllowed={item.destination.details?.threeLegsAllowed}
        equipmentTypes={item.destination.details?.equipmentTypes}
        nextButtonLabel={showFinishOnRailCarrierReview ? "Finish" : "Next"}
        onBack={() => {
          setShowRailCarrierReviewPanel(false);
          setShowRailStationLookupPanel(true);
        }}
        onClose={() => {
          setShowRailCarrierReviewPanel(false);
          setShowRailStationLookupPanel(false);
        }}
        onToggleCarrier={handleLookupCarrierToggle}
        onAddCarrier={() => {
          setShowRailCarrierReviewPanel(false);
          setShowRailAddCarrierPanel(true);
        }}
        onNext={async () => {
          if (showFinishOnRailCarrierReview || isLookupDisplayStationsEmpty) {
            await handleLookupCarrierReviewNext();
            return;
          }

          setPrefilledRailStation(selectedLookupStation);
          setShowRailCarrierReviewPanel(false);
          setShowRailStationLookupPanel(false);
          setAddModeModalOpen("RAIL");
        }}
        onFinishSuccess={async () => {
          const refreshedDestinationDetails = await getLocationDetailsWithRetry(
            item.destination.id,
          );

          setLocalDestinationModesOverride(
            toModes(
              refreshedDestinationDetails?.inboundModes || [],
              refreshedDestinationDetails?.outboundModes || [],
            ) as any,
          );

          await refreshCurrentDestinationQuotes();
          triggerRefetchMultiLocation();
        }}
        onFinishError={(error) => {
          failedNotification(
            "Failed to save rail station selection.",
            (error as any)?.message || "Unknown error",
          );
        }}
      />

      <RailStationAddCarrierPanel
        open={showRailAddCarrierPanel}
        initialSelectedCarriers={Object.keys(lookupRailCarriers).filter(
          (carrier) => lookupRailCarriers[carrier],
        )}
        onBack={(selectedCarriers) => {
          const existingCarrierNames = Object.keys(lookupRailCarriers);
          const newlyAdded = selectedCarriers.filter(
            (carrier) => !existingCarrierNames.includes(carrier),
          );
          if (newlyAdded.length > 0) {
            setLookupUserAddedCarriers((prev) =>
              Array.from(new Set([...prev, ...newlyAdded])),
            );
          }

          setLookupRailCarriers((prev) => {
            const next = { ...prev };
            selectedCarriers.forEach((carrier) => {
              next[carrier] = true;
            });
            return next;
          });
          setShowRailAddCarrierPanel(false);
          setShowRailCarrierReviewPanel(true);
        }}
        onApplySelected={async (selectedCarriers) => {
          const existingCarrierNames = Object.keys(lookupRailCarriers);
          const newlyAdded = selectedCarriers.filter(
            (carrier) => !existingCarrierNames.includes(carrier),
          );

          if (selectedCarriers.length > 0 && selectedLookupStation) {
            const marketerName = buildMarketerName(
              auth.user?.profile?.given_name,
              auth.user?.profile?.family_name,
            );

            await NitroService.createCarrierForStation({
              stationState: selectedLookupStationState || selectedLookupStation,
              carriers: selectedCarriers,
              username: marketerName,
            });
          }

          if (newlyAdded.length > 0) {
            setLookupUserAddedCarriers((prev) =>
              Array.from(new Set([...prev, ...newlyAdded])),
            );
          }

          setLookupRailCarriers((prev) => {
            const next = { ...prev };
            selectedCarriers.forEach((carrier) => {
              next[carrier] = true;
            });
            return next;
          });
          setShowFinishOnRailCarrierReview(true);
          setShowRailAddCarrierPanel(false);
          setShowRailCarrierReviewPanel(true);
        }}
        onClose={() => {
          setShowRailAddCarrierPanel(false);
          setShowRailCarrierReviewPanel(true);
        }}
      />

      <AddModeModal
        addModeModalOpen={addModeModalOpen}
        savingRail={savingRail}
        railStation={railStation}
        loadingRailStations={loadingRailStations}
        railStations={railStations}
        loadingCarriers={loadingCarriers}
        railCarriers={railCarriers}
        onClose={() => setAddModeModalOpen(null)}
        onRailStationChange={handleRailStationChange}
        onToggleRailCarrier={toggleRailCarrier}
        onSaveRail={handleSaveRail}
      />
      <OccupantPickerModal
        modalState={addMissingProductModalState}
        selectedOccupantKeys={selectedOccupantKeys}
        submitting={submittingAddMissingProduct}
        onClose={closeAddMissingProductModal}
        onToggleOccupant={toggleSelectedOccupant}
        onSubmit={submitAddMissingProductForSelectedOccupants}
      />
      <ProductSelectionSheet
        open={isFilterOpen}
        priorityProducts={priorityProducts}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        onClose={() => setIsFilterOpen(false)}
      />

      <MonthSelectionSheet
        open={isMonthPickerOpen}
        months={months}
        selectedMonth={selectedMonth}
        setSelectedMonth={setSelectedMonth}
        onClose={() => setIsMonthPickerOpen(false)}
      />

      <div className="quote-panel-header">
        <button
          type="button"
          id="quote-detail-back-button"
          onClick={onBack}
          className="icon-button-circle"
        >
          <img
            data-tooltip-id="go-back-icon-quote-details"
            src={icons.BackWithBackgroundIcon}
            alt="Go Back Icon"
            className="back-icon"
          />
        </button>
        <div id="quote-detail-header-title" className="header-title">
          Quote
        </div>
        {/* <button type="button" className="icon-button-circle">
          <img src={icons.FilterIcon} alt="Filter Icon" />
        </button> */}
      </div>

      <div id="quote-details-panel-1" className="quote-panel-content">
        <div>
          <div
            id="detail-destination-header"
            className="detail-destination-header"
          >
            <span id="quote-detail-destination-marker">
              <NitroMarkerBase
                width={36}
                height={48}
                type={item.destination.pinType || ""}
              />
            </span>

            <span className="location-name-text-styled">
              {item.destination.name}
              <span
                className="info-circle"
                onClick={handleOpenInsightsPanel}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleOpenInsightsPanel();
                  }
                }}
              >
                <img
                  id="quote-details-insights-info-icon"
                  data-tooltip-id="InformationIcon"
                  src={icons.InformationIcon}
                  alt="InformationIcon"
                  className="info-icon"
                />
              </span>
            </span>
          </div>

          <div className="offer-subheadline-styled">Your Offer</div>

          <div
            id="card-metrics-row-best-offer"
            className="card-metrics-row-quote-details"
          >
            <div className="metric-col">
              <span className="metric-label">DLVD Offer</span>
              <span className="metric-value-lg">
                ${bestOffer?.deliveredPrice ?? item.summary.deliveredPrice}
              </span>
            </div>
            <div className="metric-col center">
              {(bestOffer?.mode ?? item.summary.mode)?.toUpperCase() ===
              "TRUCK" ? (
                <TruckIconFO color="#60A5FA" />
              ) : (
                <div className="summary-rail-mode-wrap">
                  <RailIconFO color="#F97316" />
                  <span id="rail-min-cars" className="metric-value-sm">
                    {(bestOffer?.mode ?? item.summary.mode)?.toUpperCase() ===
                      "RAIL" &&
                      ((bestOffer?.freight ?? item.summary.freight) &&
                      typeof (
                        bestOffer?.minCars ?? (item.summary as any).minCars
                      ) === "number" &&
                      (bestOffer?.minCars ?? (item.summary as any).minCars) >
                        1 ? (
                        <>
                          ({bestOffer?.minCars ?? (item.summary as any).minCars}
                          +)
                        </>
                      ) : (
                        "1+"
                      ))}
                  </span>
                </div>
              )}
              <span className="metric-value-sm">
                ${bestOffer?.freight ?? item.summary.freight}
              </span>
            </div>
            <div className="metric-col right">
              <span id="origin-name-quote-details" className="origin-link">
                {bestOffer?.originName ?? item.summary.originName}
              </span>
              <span className="metric-value-lg">
                ${bestOffer?.netback ?? item.summary.netback}
              </span>
            </div>
          </div>
        </div>

        <div
          id="all-origins-section-quote-details"
          className="all-origins-section"
        >
          <h3 className="all-origins-section-title">All Origins</h3>
          <AllOriginsHeader
            dlvdOfferInput={dlvdOfferInput}
            onDlvdOfferChange={handleDlvdOfferChange}
            onResetClick={handleResetClick}
            kochDlvdMode={kochDlvdMode}
            onKochDlvdModeChange={handleKochDlvdModeChange}
            showKochDlvdOfferSelection={originManager}
            isSavingDlvdOptions={isSavingDlvdOptions}
          />

          <div className="quotes-list-wrapper" id="quote-details-all-origins">
            {loading ? (
              <div className="loading-container">
                <LoadingIcon height={60} width={60} color="#60A5FA" />
              </div>
            ) : (
              quotesWithFreightForDisplay.map(({ quote, renderKey }) => (
                <MultiLocQuoteRow
                  key={renderKey}
                  quote={quote}
                  destinationItem={item.destination}
                  originManager={originManager}
                  kochDlvdMode={kochDlvdMode}
                  nbOverrides={nbOverrides}
                  dlvdOfferInput={dlvdOfferInput}
                  editableRefs={editableRefs}
                  setHoveringFreight={() => {}}
                  setDisplayFreightDetailsPanel={setDisplayFreightDetailsPanel}
                  setDisplayTruckFreightDetailsPanel={
                    setDisplayTruckFreightDetailsPanel
                  }
                  setSelectedTruckDetailRecord={setSelectedTruckDetailRecord}
                  setAnchorForTruckDetailsPanel={setAnchorForTruckDetailsPanel}
                  handleNbEdit={handleNbEdit}
                  debounceTimers={debounceTimers}
                />
              ))
            )}
          </div>
        </div>

        {!loading &&
          quotesWithoutFreight.length > 0 &&
          (shouldShowTruckMissing || shouldShowRailMissing) && (
            <MultiLocQuoteMissingOrigin
              loading={loading}
              hasQuotesWithoutFreight={quotesWithoutFreight.length > 0}
              shouldShowTruckMissing={shouldShowTruckMissing}
              shouldShowRailMissing={shouldShowRailMissing}
              missingFreightTab={missingFreightTab}
              setMissingFreightTab={setMissingFreightTab}
              originsMissingProductForActiveTab={
                originsMissingProductForActiveTab
              }
              showMissingProductHeader={showMissingProductHeader}
              selectedProduct={selectedProduct}
              fallbackProduct={product}
              loadingAddProductByKey={loadingAddProductByKey}
              onReviewResolveClick={handleReviewResolveClick}
              onAddMissingProduct={handleAddMissingProduct}
              missingMilesOrigins={missingMilesOrigins}
              loadingMileageRequestByOrigin={loadingMileageRequestByOrigin}
              onMileageRequest={handleMileageRequest}
              loadingMissingMiles={loadingMissingMiles}
              originsWithNoGuidance={originsWithNoGuidance}
              manageRailRate={manageRailRate}
              railMissingQuotesForDisplay={railMissingQuotesForDisplay}
              originsMissingRailFreightForDisplay={
                originsMissingRailFreightForDisplay
              }
              requestedRailRateByOriginKey={requestedRailRateByOriginKey}
              failedRailRateRequestByOriginKey={
                failedRailRateRequestByOriginKey
              }
              loadingRailRateRequestByOriginKey={
                loadingRailRateRequestByOriginKey
              }
              publicTariffStatusByOriginKey={publicTariffStatusByOriginKey}
              publicTariffRateByOriginKey={publicTariffRateByOriginKey}
              onRequestMultipleRailRates={() =>
                setShowRequestMultiplePanel(true)
              }
              onRequestSingleRailOrigin={handleRequestSingleRailOrigin}
              onCheckPublicTariffForOrigin={handleCheckPublicTariffForOrigin}
              loadingRailMissingFreight={loadingRailMissingFreight}
            />
          )}

        {!loading &&
          (item.destination as any).pinType !== "city" &&
          (!hasTruckInbound || !hasRailInbound) && (
            <div id="inbound-modes-section" className="inbound-modes-section">
              <div className="inbound-modes-title">
                Can This Location Support More Inbound Modes?
              </div>

              <div className="inbound-card">
                {!hasTruckInbound && (
                  <div id="add-inbound-truck-id">
                    <div className="add-mode-row">
                      <div className="icon-text-row">
                        <TruckIconFO color="#60A5FA" />
                        <span className="mode-label">Truck</span>
                      </div>
                      <button
                        id="add-inbound-truck"
                        type="button"
                        className="add-inbound-btn add-inbound-btn-offset"
                        onClick={handleAddInboundTruck}
                      >
                        <span
                          id="add-inbound-rail-button-mobile"
                          className="add-inbound-text"
                        >
                          + Add Inbound Truck
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {!hasRailInbound && (
                  <div
                    id="add-inbound-rail-id"
                    className={
                      !hasTruckInbound ? "inbound-card-with-top-space" : ""
                    }
                  >
                    <div className="add-mode-row">
                      <div
                        id="quote-inbound-rail-icon-text"
                        className="icon-text-row"
                      >
                        <RailIconFO color="#F97316" />
                        <span
                          id="quote-inbound-rail-label"
                          className="truck-setup-origin-name"
                        >
                          {/* {item.summary.originName || "Rail"} */}
                          Rail
                        </span>
                      </div>
                      <button
                        id="add-inbound-rail"
                        type="button"
                        className="add-inbound-btn add-inbound-btn-offset"
                        onClick={handleAddInboundRailClick}
                      >
                        <span
                          id="add-inbound-rail-button-mobile"
                          className="add-inbound-text"
                        >
                          + Add Inbound Rail
                        </span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
      </div>

      <div className="floating-footer">
        <div className="quote-sub-footer-row">
          <FooterSelectorButtons
            selectedProduct={selectedProduct}
            selectedMonth={selectedMonth}
            months={months}
            onProductClick={() => setIsFilterOpen(!isFilterOpen)}
            onMonthClick={() => setIsMonthPickerOpen(!isMonthPickerOpen)}
          />
        </div>

        <div id="search-bar-quote-detail-panel" className="footer-main-row">
          <button
            id="open-origins-details"
            type="button"
            className="origins-btn-circle"
            onClick={onOpenOrigins}
          >
            <img src={icons.BuildIconWhiteBackground} alt="Build Icon" />
            <div className="origin-text">Origins</div>
          </button>

          <div
            id="quote-detail-search-pill"
            className="search-pill"
            onClick={onSearchClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") onSearchClick?.();
            }}
          >
            <SearchIcon className="search-icon-sm" />
            <span id="search-pill-text" className="search-pill-text">
              {companyName || item.destination.name}
            </span>
          </div>
        </div>
      </div>

      {troubleshootQuoteOpen && (
        <>
          <div className="fullscreen-background-overlay" />
          <div className="troubleshoot-quote-overlay troubleshoot-quote-overlay-active">
            <div className="troubleshoot-quote-content">
              <TroubleshootQuote />
            </div>
          </div>
        </>
      )}

      {displayRailFreightDetailsPanel && <FreightDetailPanelQMD />}

      {displayTruckFreightDetailsPanel && selectedTruckDetailRecord && (
        <>
          <div className="fullscreen-background-overlay" />
          <TruckFreightDetailPanel {...selectedTruckDetailRecord} isMobile />
        </>
      )}

      <RailRateRequestPanel
        open={showRailRateRequestPanel}
        originName={selectedRailRateRequestOriginName}
        origins={selectedRailRateRequestOrigins}
        submitOrigins={selectedRailRateRequestSubmitOrigins}
        showOriginsSection={selectedRailRateRequestOrigins.length > 0}
        destinationName={item.destination.name || ""}
        onClose={() => {
          setShowRailRateRequestPanel(false);
          setPendingRailRateRequestOriginKeys([]);
          setSelectedRailRateRequestSubmitOrigins([]);
        }}
        onSubmit={handleRailRateRequestSubmit}
      />

      <RequestMultipleRailRatesPanel
        open={showRequestMultiplePanel}
        origins={originsMissingRailFreightForDisplay}
        requestedRailRateByOriginKey={requestedRailRateByOriginKey}
        onClose={() => setShowRequestMultiplePanel(false)}
        onNext={async (selectedOrigins) => {
          const selectedOriginNames = selectedOrigins.map(
            (origin) => origin.originName,
          );
          const selectedVisibleOriginNames = selectedOrigins
            .filter((origin) => origin.competitor !== true)
            .map((origin) => origin.originName);
          const selectedOriginKeys = selectedOrigins.map(
            (origin) => origin.originKey,
          );
          const allSelectedAreCompetitorOrigins =
            selectedOrigins.length > 0 &&
            selectedOrigins.every((origin) => origin.competitor === true);

          if (allSelectedAreCompetitorOrigins) {
            setShowRequestMultiplePanel(false);
            setShowRailRateRequestPanel(false);
            await handleRailRateRequestSubmit(
              {
                businessOpportunity: "one-time",
                volume: "",
                targetRate: "",
                sourcingAndLogistics: "",
                comments: "",
              },
              selectedOriginKeys,
            );
            return;
          }

          setSelectedRailRateRequestOriginName("Multiple Origins");
          setSelectedRailRateRequestOrigins(selectedVisibleOriginNames);
          setSelectedRailRateRequestSubmitOrigins(selectedOriginNames);
          setPendingRailRateRequestOriginKeys(selectedOriginKeys);
          setShowRequestMultiplePanel(false);
          setShowRailRateRequestPanel(true);
        }}
      />
    </div>
  );
};

export default MultiLocQuoteDetailsPanel;
